import React from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, TextField, Typography } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import {
  DIVE_TYPES,
  FIELD_HELP,
  fieldLabel,
  MAX_ROUND,
  WINDOW_MODES,
} from "../../../utils/ruleDraft";
import { useRuleStyles } from "../styles";

const numberInputProps = (spec) => {
  const props = { inputMode: spec.type === "DECIMAL" ? "decimal" : "numeric" };
  if (spec.min != null) props.min = spec.min;
  if (spec.max != null) props.max = spec.max;
  props.step =
    spec.step != null ? spec.step : spec.type === "DECIMAL" ? 0.1 : 1;
  return props;
};

/**
 * Scope controls (rounds window, voluntary/optional filter) and the fields of one rule type,
 * generated from the catalog's field specs. Only what the type supports is rendered, and the
 * controls a type requires offer no "All" choice (§8.2 `windowRequired` / `optionalRequired`).
 */
const RuleFieldsForm = ({
  type,
  form,
  onChange,
  categories,
  heights,
  fullCodeModes,
  errors,
  disabled,
}) => {
  const classes = useRuleStyles();
  if (!type) return null;

  const setValue = (name, value) =>
    onChange({ ...form, values: { ...form.values, [name]: value } });

  const renderField = (spec) => {
    const label = fieldLabel(type.key, spec.name);
    const value = form.values?.[spec.name] ?? "";
    const common = {
      key: spec.name,
      id: `rule-field-${spec.name}`,
      label,
      variant: "outlined",
      size: "small",
      value,
      required: spec.required,
      disabled,
      error: Boolean(errors[spec.name]),
      helperText: errors[spec.name] || FIELD_HELP[spec.name] || " ",
      fullWidth: true,
    };
    switch (spec.type) {
      case "CATEGORY":
        return (
          <TextField
            {...common}
            select
            onChange={(e) => setValue(spec.name, e.target.value)}
          >
            {categories.map((c) => (
              <MenuItem key={c.key} value={String(c.key)}>
                {c.key} — {c.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case "HEIGHT":
        return (
          <TextField
            {...common}
            select
            onChange={(e) => setValue(spec.name, e.target.value)}
          >
            {heights.map((h) => (
              <MenuItem key={h.key} value={String(h.key)}>
                {h.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case "FULL_CODE_MODE":
        return (
          <TextField
            {...common}
            select
            onChange={(e) => setValue(spec.name, e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Dive number only (default)</MenuItem>
            {fullCodeModes.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case "DIVE_BANK":
        return (
          <TextField
            {...common}
            multiline
            minRows={3}
            onChange={(e) => setValue(spec.name, e.target.value)}
            InputProps={{ className: classes.bank }}
            inputProps={{ spellCheck: false }}
          />
        );
      case "INTEGER":
      case "DECIMAL":
      default:
        return (
          <TextField
            {...common}
            type="number"
            onChange={(e) => setValue(spec.name, e.target.value)}
            inputProps={numberInputProps(spec)}
          />
        );
    }
  };

  const showWindow = type.supportsWindow;
  const showDiveType = type.supportsOptionalFilter;
  const rangeOn = form.windowMode === WINDOW_MODES.RANGE;

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      {(showWindow || showDiveType) && (
        <Box className={classes.scopeGrid}>
          {showWindow && (
            <Box>
              <Typography id="rounds-label" className={classes.fieldGroupLabel}>
                Rounds{type.windowRequired ? " (this rule needs a range)" : ""}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                aria-labelledby="rounds-label"
                value={form.windowMode}
                onChange={(_, next) => {
                  if (!next || disabled) return;
                  onChange({ ...form, windowMode: next });
                }}
              >
                {!type.windowRequired && (
                  <ToggleButton
                    value={WINDOW_MODES.ALL}
                    className={classes.toggle}
                    aria-label="All rounds"
                    disabled={disabled}
                  >
                    All rounds
                  </ToggleButton>
                )}
                <ToggleButton
                  value={WINDOW_MODES.RANGE}
                  className={classes.toggle}
                  aria-label="A range of rounds"
                  disabled={disabled}
                >
                  Range
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          {showDiveType && (
            <Box>
              <Typography
                id="dive-type-label"
                className={classes.fieldGroupLabel}
              >
                Dive type
                {type.optionalRequired ? " (this rule needs one kind)" : ""}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                aria-labelledby="dive-type-label"
                value={form.diveType}
                onChange={(_, next) => {
                  if (!next || disabled) return;
                  onChange({ ...form, diveType: next });
                }}
              >
                {!type.optionalRequired && (
                  <ToggleButton
                    value={DIVE_TYPES.ALL}
                    className={classes.toggle}
                    aria-label="All dives"
                    disabled={disabled}
                  >
                    All
                  </ToggleButton>
                )}
                <ToggleButton
                  value={DIVE_TYPES.VOLUNTARY}
                  className={classes.toggle}
                  aria-label="Voluntary dives only"
                  disabled={disabled}
                >
                  Voluntary
                </ToggleButton>
                <ToggleButton
                  value={DIVE_TYPES.OPTIONAL}
                  className={classes.toggle}
                  aria-label="Optional dives only"
                  disabled={disabled}
                >
                  Optional
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
      )}

      {showWindow && rangeOn && (
        <Box className={classes.scopeGrid}>
          <TextField
            id="rule-from-round"
            label="From round"
            type="number"
            variant="outlined"
            size="small"
            required
            disabled={disabled}
            value={form.startIndex}
            onChange={(e) => onChange({ ...form, startIndex: e.target.value })}
            inputProps={{ min: 1, max: MAX_ROUND, inputMode: "numeric" }}
            error={Boolean(errors.startIndex)}
            helperText={errors.startIndex || " "}
          />
          <TextField
            id="rule-to-round"
            label="Through round"
            type="number"
            variant="outlined"
            size="small"
            required
            disabled={disabled}
            value={form.endIndex}
            onChange={(e) => onChange({ ...form, endIndex: e.target.value })}
            inputProps={{ min: 1, max: MAX_ROUND, inputMode: "numeric" }}
            error={Boolean(errors.endIndex)}
            helperText={errors.endIndex || " "}
          />
        </Box>
      )}

      {type.fields?.length > 0 && (
        <Box className={classes.scopeGrid}>{type.fields.map(renderField)}</Box>
      )}
    </Box>
  );
};

const lookupShape = PropTypes.arrayOf(
  PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
);

RuleFieldsForm.propTypes = {
  type: PropTypes.shape({
    key: PropTypes.string,
    supportsWindow: PropTypes.bool,
    windowRequired: PropTypes.bool,
    supportsOptionalFilter: PropTypes.bool,
    optionalRequired: PropTypes.bool,
    fields: PropTypes.array,
  }),
  form: PropTypes.shape({
    windowMode: PropTypes.string,
    startIndex: PropTypes.string,
    endIndex: PropTypes.string,
    diveType: PropTypes.string,
    values: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  categories: lookupShape,
  heights: lookupShape,
  fullCodeModes: lookupShape,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
};

RuleFieldsForm.defaultProps = {
  type: null,
  categories: [],
  heights: [],
  fullCodeModes: [],
  errors: {},
  disabled: false,
};

export default RuleFieldsForm;
